import React, { useState, useEffect } from 'react';
import SafeIcon from '../../common/SafeIcon';
import { useCRM } from '../../context/CRMContext';

const CustomerRoomModal = ({ company, onClose }) => {
  const { customerRooms, addCustomerRoom, updateCustomerRoom, addDocumentToRoom, deleteDocumentFromRoom } = useCRM();
  const existingRoom = customerRooms.find(r => r.company_id === company.company_id);
  const [activeTab, setActiveTab] = useState('settings');
  const [formData, setFormData] = useState({
    welcome_title: `Velkommen til ${company.name}`,
    welcome_message: 'Vi ser frem til et godt samarbeid! Her finner dere relevant informasjon og dokumenter.',
    password: '',
  });
  const [newDocData, setNewDocData] = useState({ name: '', type: 'contract', category: 'Legal', expiry: '' });
  const [copied, setCopied] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (existingRoom) {
      setFormData({
        welcome_title: existingRoom.welcome_title || '',
        welcome_message: existingRoom.welcome_message || '',
        password: existingRoom.password || ''
      });
    } else {
      setFormData(prev => ({ ...prev, password: Math.random().toString(36).slice(-8) }));
    }
  }, [existingRoom, company]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (existingRoom) {
      updateCustomerRoom(existingRoom.room_id, formData);
    } else {
      addCustomerRoom({ company_id: company.company_id, ...formData, documents: [] });
    }
    onClose();
  };

  const handleAddDocument = (e) => {
    e.preventDefault();
    if (!existingRoom) return;
    addDocumentToRoom(existingRoom.room_id, { ...newDocData, size: `${(Math.random() * 5 + 0.5).toFixed(1)} MB` });
    setNewDocData({ name: '', type: 'contract', category: 'Legal', expiry: '' });
  };

  const getPortalLink = () => {
    if (!existingRoom) return '';
    return `${window.location.origin}/#/portal/${existingRoom.room_id}`;
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(getPortalLink());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-8 p-1">
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-6 border border-slate-700 shadow-xl overflow-hidden relative">
        <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -mr-16 -mt-16 blur-xl"></div>
        <div className="flex items-center gap-5 relative z-10">
          <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center text-white shadow-inner border border-white/10 backdrop-blur-sm">
            <SafeIcon icon="🛡️" className="w-7 h-7" />
          </div>
          <div>
            <h4 className="text-xl font-black text-white tracking-tight">Secure Data Room</h4>
            <p className="text-sm text-slate-300 font-medium mt-1">Administrer tilgang og filer for {company.name}</p>
          </div>
        </div>
      </div>

      {existingRoom && (
        <div className="flex p-1.5 bg-slate-100 rounded-xl border border-slate-200">
          <button onClick={() => setActiveTab('settings')} className={`flex-1 flex items-center justify-center gap-2 py-3 text-xs font-black uppercase tracking-widest rounded-lg transition-all ${activeTab === 'settings' ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200' : 'text-slate-500 hover:text-slate-700'}`}>
            <SafeIcon icon="⚙️" className="w-3.5 h-3.5" /> Innstillinger
          </button>
          <button onClick={() => setActiveTab('documents')} className={`flex-1 flex items-center justify-center gap-2 py-3 text-xs font-black uppercase tracking-widest rounded-lg transition-all ${activeTab === 'documents' ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200' : 'text-slate-500 hover:text-slate-700'}`}>
            <SafeIcon icon="📂" className="w-3.5 h-3.5" /> Dokumenter ({existingRoom.documents?.length || 0})
          </button>
        </div>
      )}

      {activeTab === 'settings' ? (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 gap-6">
            <div className="space-y-2">
              <label className="text-xs font-black text-slate-500 uppercase tracking-wider">Overskrift Portal</label>
              <input required className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-primary-500 transition-all outline-none font-bold placeholder-slate-300" value={formData.welcome_title} onChange={(e) => setFormData({ ...formData, welcome_title: e.target.value })} />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-black text-slate-500 uppercase tracking-wider">Velkomstmelding</label>
              <textarea required rows="3" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-primary-500 transition-all outline-none font-medium text-sm placeholder-slate-300" value={formData.welcome_message} onChange={(e) => setFormData({ ...formData, welcome_message: e.target.value })} />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-black text-slate-500 uppercase tracking-wider">Sikkerhet (Passord)</label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <SafeIcon icon="🔒" className="h-4 w-4 text-slate-400" />
                  </div>
                  <input type={showPassword ? "text" : "password"} required className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-primary-500 transition-all outline-none font-bold text-slate-900" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} />
                </div>
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="px-4 bg-slate-100 border border-slate-200 rounded-xl text-slate-600 font-bold text-xs hover:bg-slate-200 uppercase tracking-widest">
                  {showPassword ? 'Skjul' : 'Vis'}
                </button>
              </div>
            </div>

            {existingRoom && (
              <div className="p-6 bg-slate-900 rounded-2xl space-y-4">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2">
                  <SafeIcon icon="🔗" className="w-3 h-3" /> Link til Kunderom
                </label>
                <div className="flex items-center gap-3">
                  <code className="flex-1 bg-white/5 p-3 rounded-xl text-xs text-white font-mono break-all border border-white/10 selection:bg-primary-500/50">
                    {getPortalLink()}
                  </code>
                  <button type="button" onClick={handleCopyLink} className="p-3 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-all border border-white/5" title="Kopier link">
                    <SafeIcon icon={copied ? "✅" : "📋"} className={`w-5 h-5 ${copied ? 'text-emerald-400' : ''}`} />
                  </button>
                  <a href={getPortalLink()} target="_blank" rel="noreferrer" className="p-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-all shadow-lg shadow-primary-900/50" title="Åpne portal">
                    <SafeIcon icon="🔗" className="w-5 h-5" />
                  </a>
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end pt-6 border-t border-slate-100">
            <button type="submit" className="px-8 py-3 bg-slate-900 text-white font-black text-sm rounded-xl hover:bg-slate-800 shadow-xl shadow-slate-200 transition-all flex items-center gap-2">
              <SafeIcon icon="💾" className="w-4 h-4" /> {existingRoom ? 'Lagre endringer' : 'Opprett Data Room'}
            </button>
          </div>
        </form>
      ) : (
        <div className="space-y-8">
          <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 space-y-5">
            <h5 className="text-xs font-black text-slate-500 uppercase tracking-widest flex items-center">
              <SafeIcon icon="☁️" className="mr-2 w-4 h-4" /> Last opp nytt dokument
            </h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="col-span-full">
                <input placeholder="Dokumentnavn (f.eks. Signert Avtale)" className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl outline-none focus:border-primary-500 font-bold text-sm" value={newDocData.name} onChange={(e) => setNewDocData({ ...newDocData, name: e.target.value })} />
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                  <SafeIcon icon="🏷️" className="w-4 h-4" />
                </div>
                <select className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl outline-none font-bold text-sm cursor-pointer text-slate-700 appearance-none" value={newDocData.category} onChange={(e) => setNewDocData({ ...newDocData, category: e.target.value })}>
                  <option value="Legal">Juridisk / Kontrakt</option>
                  <option value="Technical">Teknisk Dokumentasjon</option>
                  <option value="Sales">Salg & Presentasjoner</option>
                  <option value="Admin">Administrasjon</option>
                </select>
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                  <SafeIcon icon="📄" className="w-4 h-4" />
                </div>
                <select className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl outline-none font-bold text-sm cursor-pointer text-slate-700 appearance-none" value={newDocData.type} onChange={(e) => setNewDocData({ ...newDocData, type: e.target.value })}>
                  <option value="contract">Avtale (Signering)</option>
                  <option value="report">Rapport</option>
                  <option value="presentation">Presentasjon</option>
                  <option value="spreadsheet">Regneark</option>
                </select>
              </div>
              <div className="relative col-span-full md:col-span-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                  <SafeIcon icon="📅" className="w-4 h-4" />
                </div>
                <input type="date" className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl outline-none font-bold text-sm text-slate-700" value={newDocData.expiry} onChange={(e) => setNewDocData({ ...newDocData, expiry: e.target.value })} placeholder="Utløpsdato (valgfri)" />
              </div>
              <div className="col-span-full md:col-span-1">
                <button onClick={handleAddDocument} disabled={!newDocData.name} className="w-full h-full px-6 py-3 bg-slate-900 text-white rounded-xl hover:bg-slate-800 font-black text-sm disabled:opacity-50 transition-all shadow-lg shadow-slate-200 border border-slate-900">
                  Last opp fil
                </button>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            {existingRoom.documents && existingRoom.documents.length > 0 ? (
              existingRoom.documents.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-2xl hover:border-primary-300 transition-all group shadow-sm">
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-xl border ${doc.category === 'Legal' ? 'bg-purple-50 text-purple-600 border-purple-100' : doc.category === 'Technical' ? 'bg-blue-50 text-blue-600 border-blue-100' : 'bg-slate-50 text-slate-600 border-slate-100'}`}>
                      <SafeIcon icon="📄" className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-bold text-slate-900 text-sm">{doc.name}</p>
                        {doc.version && <span className="px-1.5 py-0.5 bg-slate-100 text-slate-500 text-[10px] font-bold rounded">v{doc.version}</span>}
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{doc.category} • {doc.size}</p>
                        {doc.downloads > 0 && (
                          <div className="flex items-center gap-1 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">
                            <SafeIcon icon="⬇️" className="w-3 h-3" /> {doc.downloads}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <button onClick={() => { if (confirm('Slette dokument?')) deleteDocumentFromRoom(existingRoom.room_id, doc.id); }} className="w-10 h-10 flex items-center justify-center text-slate-300 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all" title="Slett fil">
                    <SafeIcon icon="🗑️" className="w-4 h-4" />
                  </button>
                </div>
              ))
            ) : (
              <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-3xl">
                <SafeIcon icon="📂" className="w-12 h-12 mx-auto text-slate-200 mb-4" />
                <p className="text-sm font-bold text-slate-400 uppercase tracking-widest">Ingen filer i rommet</p>
                <p className="text-xs text-slate-400 mt-1">Last opp filer for å dele med kunden.</p>
              </div>
            )}
          </div>
          <div className="flex justify-end pt-4 border-t border-slate-100">
            <button onClick={onClose} className="px-6 py-2 text-slate-500 font-black text-xs uppercase tracking-[0.2em] hover:text-slate-900 transition-all">
              Lukk
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerRoomModal;